/**
 * campaignEvents — the single source of truth for the site's timed "save the
 * date" popups. EventPopup.tsx shows whichever campaign is live right now, then
 * auto-retires it once its window closes. Adding a future event is a new entry
 * here; no component changes, no cleanup later.
 *
 * These mirror the App Store Connect in-app events, with the website teasing a
 * couple of days AHEAD of the store:
 *   showFrom  = ~2 days BEFORE the ASC publish date (website leads the store)
 *   eventDate = the ASC event START (countdown target; "live" copy after)
 *   hideAfter = the ASC event END (popup retires)
 *
 * TIMEZONE: the ASC events are set in (GMT-4) New York, so every timestamp
 * carries an explicit `-04:00` offset (EDT — valid for all of Jul–Sep 2026).
 * That makes the countdown target the SAME real instant for every visitor,
 * anywhere in the world — not their local wall-clock. If you ever add a
 * winter event, use `-05:00` (EST).
 *
 * The appstore CTA `href` is the event's own App Store URL (?eventid=…), so
 * the button deep-links straight to the event card on the store.
 *
 * Per-event dismissal + a short global cooldown are handled in EventPopup, not
 * here. Keep this array chronological; on overlap the earlier entry wins.
 */

export type CampaignCta =
  | { kind: 'appstore'; href?: string } // black App Store button; href = event URL
  | { kind: 'link'; href: string; label: string } // internal link (e.g. a guide)

export interface CampaignEvent {
  /** Stable id — also the localStorage dismissal key. Never reuse across events. */
  id: string
  /** Optional start gate. Omit to allow the popup any time before hideAfter. */
  showFrom?: string
  hideAfter: string
  /** ASC publish date. The event-card CTA URL (?eventid=) 404s until this
   *  moment, so before it the button falls back to the plain app page. */
  publishAt?: string
  /** Optional countdown target. Omit for a seasonal window (no countdown). */
  eventDate?: string
  /** Small uppercase line — mirrors the App Store event badge. */
  eyebrow: string
  /** Headline lead-in; `accent` is appended in gold on the same line. */
  headline: string
  accent?: string
  /** Teaser line — the ASC short description. */
  body: string
  /** Optional richer line (the ASC long description) shown once the event is live. */
  liveBody?: string
  cta: CampaignCta
}

//   = non-breaking space, keeps a date/number from wrapping awkwardly.
export const CAMPAIGN_EVENTS: CampaignEvent[] = [
  {
    // ASC in-app event: "Plastic Free July" (Challenge) · eventid 6790543336.
    // Publish Jul 17 8PM ET · Active Jul 19 8PM – Jul 31 7:59 PM ET.
    id: 'plastic-free-july-2026',
    showFrom: '2026-07-15T00:00-04:00', // ~2 days before publish
    publishAt: '2026-07-17T20:00-04:00',
    eventDate: '2026-07-19T20:00-04:00',
    hideAfter: '2026-07-31T19:59-04:00',
    eyebrow: 'Challenge',
    headline: 'Plastic Free',
    accent: 'July.',
    body: 'Scan your daily habits and cut the plastic.',
    liveBody: 'Join Plastic Free July. Scan what you eat and drink, find lower-plastic swaps, and watch your exposure drop.',
    cta: { kind: 'appstore', href: 'https://apps.apple.com/us/app/id6741938548?eventid=6790543336' },
  },
  {
    // ASC in-app event: "A Whole New MicroPlastics" (Major Update) · eventid 6790543145.
    // Publish Jul 31 8PM ET · Active Aug 2 8PM – Aug 17 7:59 PM ET.
    id: 'whole-new-microplastics-2026',
    showFrom: '2026-07-29T00:00-04:00', // ~2 days before publish
    publishAt: '2026-07-31T20:00-04:00',
    eventDate: '2026-08-02T20:00-04:00',
    hideAfter: '2026-08-17T19:59-04:00',
    eyebrow: 'Major Update',
    headline: 'A whole new',
    accent: 'MicroPlastics.',
    body: 'Redesigned, faster, and easier to scan.',
    liveBody: 'A cleaner look, instant scanning with no account, your Plastic Passport, and swaps matched to what you scan.',
    cta: { kind: 'appstore', href: 'https://apps.apple.com/us/app/id6741938548?eventid=6790543145' },
  },
  {
    // ASC in-app event: "Lunchbox Check" (Challenge) · eventid 6790543243.
    // Publish Aug 4 8PM ET · Active Aug 16 8PM – Sep 7 7:59 PM ET.
    id: 'lunchbox-check-2026',
    showFrom: '2026-08-02T00:00-04:00', // ~2 days before publish
    publishAt: '2026-08-04T20:00-04:00',
    eventDate: '2026-08-16T20:00-04:00',
    hideAfter: '2026-09-07T19:59-04:00',
    eyebrow: 'Challenge',
    headline: 'Lunchbox',
    accent: 'Check.',
    body: 'Scan the lunchbox before term starts.',
    liveBody: 'Bottles, containers and wraps carry more plastic than you think. Scan the lunchbox and swap the worst.',
    cta: { kind: 'appstore', href: 'https://apps.apple.com/us/app/id6741938548?eventid=6790543243' },
  },
]
